import { INIT_YEARS_TO_QUERY } from 'store/testResults/constants';

export const HISTORICAL_VITALS = {
  loading: {
    title: "Visualizing your vitals - this shouldn't take long."
  },
  empty: {
    title: 'Your vitals at a glance.',
    subtitle: 'The next time you have an appointment your vitals will appear here.'
  },
  getErrorState: (type: string) => `Sorry, there was an error loading your ${type} data!`
};

export const BLOOD_PRESSURE = {
  loading: {
    title: "Visualizing your Blood Pressure - this shouldn't take long."
  },
  error: {
    title: "Sorry, your Blood Pressure didn't load.",
    subtitle: 'Thanks for your patience, try again in a bit!'
  },
  empty: {
    title: 'Your Blood Pressure at a glance.',
    subtitle: 'The next time you have an appointment your vitals will appear here.'
  }
};

export const MEDICATION_LIST_DRAWERS = [
  {
    title: 'Current Medications',
    body: 'Daily, over-the-counter, and as-needed medications',
    icon: 'keyboard-arrow-right',
    mobileRouteName: 'MedicationsScreen',
    webRoutePath: '/u/health-record/current-medications',
    params: {
      medicationType: 'current'
    }
  },
  {
    title: 'Past Medications',
    body: 'Complete list of past medications',
    icon: 'keyboard-arrow-right',
    mobileRouteName: 'MedicationsScreen',
    webRoutePath: '/u/health-record/past-medications',
    params: {
      medicationType: 'past'
    }
  }
];

export const HEALTH_RECORDS_DRAWERS = {
  VisitSummary: {
    title: 'Visit Summaries',
    subtitle: (count: number) => `${count} available`,
    loading: 'Gathering your visit details. Bear with us for a moment!'
  },

  TestResults: {
    title: 'Test Results',
    subtitle: (count: number) => `${count} available from the past ${INIT_YEARS_TO_QUERY} years`,
    loading: 'Gathering your test results.'
  },

  Medications: {
    title: (isVisitSummary: boolean) => (isVisitSummary ? 'Medication Updates' : 'Medication List'),
    subtitle: () => 'View and renew your medications',
    loading: 'Finding your medications.'
  },

  PreventiveCare: {
    title: 'Preventive Care',
    subtitle: (count: number) =>
      `You have ${count} appointment${count === 1 ? '' : 's'} to schedule`,
    loading: 'Checking for preventive tasks'
  },

  MedicalHistory: {
    title: 'Medical History',
    subtitle: () => 'List of historical procedures etc.'
  },

  ClinicNotes: {
    title: 'Clinic Notes and Documents',
    subtitle: (qtyNotes: number) => `${qtyNotes} available`,
    loading: 'Checking for notes and documents',
    error: "We couldn't find any notes or documents",
    empty: 'No notes or documents to show at this time'
  },

  Vitals: {
    title: 'Vitals',
    subtitle: (lastUpdated: string) =>
      lastUpdated ? `Updated on ${lastUpdated}` : '0 available from the past 2 years',
    loading: "Vitals – We're checking for a pulse.",
    error: "We couldn't find your vitals."
  },

  Goals: {
    title: 'Goals',
    subtitle: (count: number | string) => `${count} available`,
    loading: 'Checking for goals.',
    error: "We couldn't find your goals",
    empty: "There's nothing here right now. You can ask your doctor about adding some goals!"
  },

  HealthRecordsSummary: {
    title: 'Health Record Summary Download',
    subtitle: 'Download/Share your key health information',
    description:
      'This summary may be useful to download in case of an emergency or to share if you visit a doctor outside Intermountain Healthcare.',
    btnTxt: 'Download summary',
    shareBtnTxt: 'Share summary',
    modalDescription:
      'Download a zip file containing PDF summary documents designed for easy viewing, or a computer-codified XML version to upload to an external electronic health record system. If you see a doctor outside of Intermountain Healthcare this XML summary may be able to be incorporated into your external electronic record.',
    pdfBtnTxt: 'Download viewable',
    xmlBtnTxt: 'Download codified',
    downloadError:
      'An error occurred while trying to download your Health Records. Please try again or contact our support team.',
    externalLink: 'https://intermountainhealthcare.org/patient-tools/patient-medical-records/'
  },

  getErrorState: (text: string) =>
    `Sorry, we couldn't find your ${text}. Try again or come back in a bit!`,

  getEmptyState: (text: string) =>
    `You don't seem to have any ${text}. Check back after your next appointment!`
} as const;

export const MEDICAL_HISTORY_DRAWERS = [
  {
    title: 'Procedure History',
    body: 'Your history of surgical procedures',
    icon: 'keyboard-arrow-right',
    routeName: 'Procedures',
    to: '/u/health-record/past-procedures'
  },
  {
    title: 'Allergies',
    body: 'Allergic reactions to medications, food, latex, etc.',
    icon: 'keyboard-arrow-right',
    routeName: 'Allergies',
    to: '/u/health-record/allergies'
  },
  {
    title: 'Social History',
    body: 'Recreational aspects of your health',
    icon: 'keyboard-arrow-right',
    routeName: 'SocialHistory',
    to: '/u/health-record/social-history'
  },
  {
    title: 'Immunizations',
    body: 'Record of all vaccines received',
    icon: 'keyboard-arrow-right',
    routeName: 'Immunizations',
    to: '/u/health-record/immunizations'
  },
  {
    title: 'Problem List',
    body: 'Illnesses, injuries, and other factors that affect your health',
    icon: 'keyboard-arrow-right',
    routeName: 'ProblemList',
    to: '/u/health-record/problems'
  }
] as const;

export const HEALTH_RECORDS_PAGES = {
  TestResults: {
    loading: 'Gathering your data.',
    error:
      "Sorry, we can't find your data. We aren't able to gather your test results right now. Try a different page, restart, or come back later."
  },

  Medications: {
    loading: 'Gathering your data.',
    error: "We couldn't find your medications",
    empty: "You don't seem to have any medications. Any new medications will show up here!"
  },

  MedicalHistory: {
    loading: 'Assembling your medical history.',
    usirButton: 'Utah School Immunization Record'
  },

  Vitals: {
    loading: 'Gathering your data.',
    getErrorState: (vital: string) =>
      `Sorry, we can't find your ${vital}. We aren't able to gather your vitals right now. Try a different page, restart, or come back later.`
  },

  getErrorState: (text: string) => `Sorry, we couldn't find your ${text} right now.`,

  getEmptyState: (text: string) => `You don't seem to have any ${text}.`
} as const;

export const visitSummaryPlaceholder = {
  id: '0',
  date: '',
  patient: '',
  location: '',
  provider: '',
  visitType: '',
  source: '',
  lastUpdated: ''
};

export const NO_USIR_MESSAGE =
  'Office school immunization records are only available for Utah residents at this time. Contact your health provider for similar records for your area.';

export const USIR_ERROR_MESSAGE =
  'We are having trouble loading your Utah School Immunization Record. Please try again or check back later.';
